import { App } from "@vue/runtime-core"
import { version } from "../../package.json"
import { IS_DEV, SENTRY_URL } from "@/config"
import { isNavigationFailure, NavigationFailureType } from "vue-router"
import { isIgnorableAsclepiusApiError } from "@common/composables/api/asclepius"
import router from "@/router"
import * as Sentry from "@sentry/vue"
import { useUserStore } from "@/store/user-store"
import { useCompanyStore } from "@/store/company-store"

const SENTRY_TRACES_SAMPLE_RATE = 1.0 // 100% of transactions are traced
const SENTRY_REPLAY_SAMPLE_RATE = 0.1 // 10% of errors get session replays

function initializeSentry(app: App): void {
  if (IS_DEV) {
    return
  }

  if (Sentry.isInitialized()) {
    console.debug("[SENTRY] Already initialized… skipping")
    return
  }

  console.debug("[SENTRY] Not initialized… initializing…")

  Sentry.init({
    app,
    dsn: SENTRY_URL,
    environment: import.meta.env.VITE_APP_ENV,
    release: version,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    // We recommend adjusting this value in production, or using tracesSampler for finer control
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysOnErrorSampleRate: SENTRY_REPLAY_SAMPLE_RATE,
    integrations: [
      // Name spans in your transactions that represent the component lifecycle events and durations.
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
          hooks: ["create", "update", "mount", "destroy"]
        }
      }),
      // Vue Router routing instrumentation to get better parameterized transaction names.
      Sentry.browserTracingIntegration({
        router,
        routeLabel: "path"
      }),
      // Video-like reproduction of what was happening in the user's browser before, during, and after the issue.
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      })
    ],
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      console.debug("[SENTRY] beforeSend")

      // Vue Router throws an error when the same path is navigated to but doesn't handle when
      // query params are modified – it treats this as a duplicate navigation event. These errors
      // do not need to go to Sentry because they're really not errors.
      // See more: https://github.com/vuejs/vue-router/issues/2872
      if (isNavigationFailure(hint.originalException, NavigationFailureType.duplicated)) {
        console.debug("[SENTRY] Duplicate navigation issue encountered… not sending that sucker to Sentry")
        return null
      }

      // Asclepius will report errors to Sentry when necessary. There's no reason to doubly report
      // them via client-side. Ignore them if the exception is an Asclepius API error.
      if (isIgnorableAsclepiusApiError(hint.originalException)) {
        console.debug("[SENTRY] Asclepius API error encountered… not sending that sucker to Sentry")
        return null
      }

      const { user } = useUserStore()

      if (user) {
        event.tags = event.tags ?? {}
        event.contexts = event.contexts ?? {}

        // Enrich the event with the logged-in user
        event.user = { id: user.id, email: user.email }

        // Enrich the event with User demographics context
        event.contexts["Gaia User"] = {
          ID: user.id,
          "User Type": user.user_type,
          "Company Admin ID": user.company_admin?.id || "N/A"
        }

        // Enrich the event with Company details context
        const { company } = useCompanyStore()
        if (company) {
          event.tags["gaia.company.id"] = company.id

          event.contexts["Gaia Company"] = {
            Company: {
              ID: company.id,
              Name: company.name,
              "Legal Name": company.legal_name
            }
          }
        }
      } else {
        event.user = undefined
      }

      return event
    }
  })
}

export default { initializeSentry }
